import React, {useContext, useEffect, useState} from 'react';
import { makeStyles } from "@material-ui/core/styles";
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import EnhancedTable from '../components/EnhancedTable';
import BUPaper from '../components/BusinessUnitPaper';
import StatCard from '../components/StatCard';
import PowerBIIcon from '../icons/PowerBI';
import {getData} from '../utils/apiUtils';
import { getCurrentYear } from '../utils/dateUtils';
import {ErrorContext} from "../utils/constants";
import {formatCurrency} from '../utils/currencyFormater';

const currentInitiativeHeadCells = [
  { id: 'id', numeric: false, disablePadding: false, label: 'Initiative Number' },
  { id: 'initiative_name', numeric: false, disablePadding: false, label: 'Initiative Name' },
  { id: 'owner', numeric: false, disablePadding: false, label: 'Initiative Owner' },
  { id: 'status', numeric: false, disablePadding: true, label: 'Status' },
  { id: 'max_date', numeric: false, disablePadding: true, label: 'Due Date', format: 'date' },
  { id: 'netCash', numeric: false, disablePadding: true, label: `Net Cash in ${getCurrentYear()}`, format: 'currency' },
];

const useStyles = makeStyles(() => ({
  header: {
    margin: '1rem 0rem'
  },
  statsLayout: {
    display: 'flex'
  }
}));


export default function TransAlta({current})  {
  const classes = useStyles();
  const { triggerError } = useContext(ErrorContext);
  const [ currentInitiatives, setCurrentInitiatives ] = useState([]);
  const [ businessUnits, setBusinessUnits ] = useState([]);
  const [ appliedFilters, setAppliedFilters ] = useState([]);
  const [ statistics, setStatistics ] = useState({});

  const onError = () => triggerError({
      title: 'Communication Error',
      description: 'We were unable to read the initiative data from the server. Please try again.',
      onRetry: () => window.location.reload(),
  });

  useEffect(() => {
    document.title = `Rise by TransAlta ${current ? 'Current': 'Past'} TransAlta Dashboard`;
  }, []);

  useEffect(() => {
    getData('/dashboards/businessunits' , setBusinessUnits, onError);
  }, []);

  useEffect(() => {
    getData(`/dashboards/currentinitiatives?current=${current}${appliedFilters.length ? `&bu=${encodeURIComponent(JSON.stringify(appliedFilters))}` : ''}` , setCurrentInitiatives, onError);
    getData(`/dashboards/statistics?current=${current}${appliedFilters.length ? `&bu=${encodeURIComponent(JSON.stringify(appliedFilters))}` : ''}`, setStatistics, onError);
  }, [current, appliedFilters]);

  const onClickFilter = (bu) => () => {
    if (appliedFilters.includes(bu)) {
      setAppliedFilters(appliedFilters.filter((filter) => filter !== bu));
    } else {
      setAppliedFilters([...appliedFilters, bu]);
    }
  };

  const getFilterBlurb = () => {
    if (appliedFilters.length) {
      return `Filtered to show the following business units: ${appliedFilters.join(', ')}.`;
    } else {
      return 'No filters applied. Use the toggle buttons below to filter results';
    }
  }

  return (
    <main>
      <Typography className={classes.header} variant='h4'>
        Filter By Business Unit/Fuel
      </Typography>
      <Typography className={classes.header} variant='body1'>
        {getFilterBlurb()}
      </Typography>
      <Grid container direction="row">
        {businessUnits.map((bu) => (
          <BUPaper
            key={bu}
            selected={appliedFilters.includes(bu)}
            onClick={onClickFilter(bu)}
            name={bu}
          />
        ))}
      </Grid>
      <Typography className={classes.header} variant='h4'>
        Program Statistics
      </Typography>
      <Typography className={classes.header} variant='body1'>
      Statistics are for current initiatives in the Confirm and Deliver phases with completion dates in the current or future year. The # of Current Initiatives total also includes initiatives at Aim and Build phases.
      </Typography>
      <div className={classes.statsLayout}>
        <StatCard title={`# of ${current ? 'Current' : 'Past'} Initiatives`} value={statistics.numberOfInitiatives} />
        <StatCard title={`Net Cash in ${getCurrentYear()}`} value={formatCurrency(statistics.netCash)} />
        <StatCard title='Productivity Funding Deployed' value={formatCurrency(statistics.productivityFunding)} />
      </div>
      <div className={classes.statsLayout}>
        <StatCard title='# of Priority Practice Values' value={statistics.numberOfPriorityPractices} />
        <StatCard title='# of Hours Returned to Business' value={statistics.hoursReturned} />
        <StatCard title='See full reporting with' link='TBD' value={<PowerBIIcon />} link={statistics.powerBILink}/>
      </div>
      <Typography className={classes.header} variant='h4'>
        {current ? 'Current' : 'Past'} Initiatives
      </Typography>
      <Typography className={classes.header} variant='body1'>
        Here is a list of {current ? 'current' : 'past'} initiatives, filtered by Business Unit or Fuel.
      </Typography>

      <EnhancedTable
        id='9a43298b-5450-426e-9027-13983429532c'
        exportFilename='Current Initiatives'
        allowExport={true}
        headCells={currentInitiativeHeadCells}
        rows={currentInitiatives}
        clickData={{url: '/ui/initiativecard/', key: 'id'}}
      />
    </main>
  );
}
